import React, { useCallback } from 'react';
import { Box, Skeleton, Tooltip } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  StatsContainer,
  StatItem,
  IconContainer,
  StatInfo,
  StatLabel,
  StatValue,
  getInventoryStatsIconSx,
} from '../../inventory/InventoryStats/InventoryStats.styles';
import { nextStatusFilterValue } from './nextStatusFilterValue';

export type StatusSummaryIconVariant =
  | 'inventory'
  | 'sales'
  | 'warning'
  | 'cost'
  | 'availability'
  | 'unassigned'
  | 'assigned'
  | 'inTransit';

export interface StatusSummaryMetric {
  key: string;
  label: string;
  icon: React.ReactNode;
  iconVariant: StatusSummaryIconVariant;
  filterValue: string;
  /** When set, the card is locked: not clickable, dimmed, lock icon and this text as tooltip. */
  disabledReason?: string;
}

export interface StatusSummaryBarProps {
  metrics: StatusSummaryMetric[];
  counts: Partial<Record<string, number>>;
  loading?: boolean;
  activeFilterValue?: string;
  onMetricClick?: (filterValue: string) => void;
  /**
   * When false, cards with empty `filterValue` (e.g. Total) are not clickable.
   * Default true — Fase A menus use Total to clear the status filter.
   */
  emptyFilterClickable?: boolean;
}

export const StatusSummaryBar: React.FC<StatusSummaryBarProps> = ({
  metrics,
  counts,
  loading = false,
  activeFilterValue = '',
  onMetricClick,
  emptyFilterClickable = true,
}) => {
  const theme = useTheme();
  const iconSxMap = getInventoryStatsIconSx(theme);
  const clickable = onMetricClick != null;

  const activateMetric = useCallback(
    (filterValue: string) => {
      if (onMetricClick == null) return;
      onMetricClick(nextStatusFilterValue(activeFilterValue, filterValue));
    },
    [activeFilterValue, onMetricClick],
  );

  if (loading) {
    return (
      <StatsContainer sx={{ marginBottom: 2 }}>
        {metrics.map((m) => (
          <StatItem key={m.key}>
            <Skeleton variant="circular" width={36} height={36} sx={{ mr: 1 }} />
            <StatInfo>
              <Skeleton variant="text" width={56} height={14} />
              <Skeleton variant="text" width={32} height={18} />
            </StatInfo>
          </StatItem>
        ))}
      </StatsContainer>
    );
  }

  return (
    <StatsContainer sx={{ marginBottom: 2 }}>
      {metrics.map((m) => {
        const isActive =
          m.filterValue === '' ? activeFilterValue === '' : activeFilterValue === m.filterValue;
        const isLocked = m.disabledReason != null;
        const cardClickable =
          !isLocked && clickable && (m.filterValue !== '' || emptyFilterClickable);

        const card = (
          <StatItem
            key={m.key}
            role={cardClickable ? 'button' : undefined}
            tabIndex={cardClickable ? 0 : undefined}
            aria-pressed={cardClickable ? isActive : undefined}
            onClick={cardClickable ? () => activateMetric(m.filterValue) : undefined}
            onKeyDown={
              cardClickable
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      activateMetric(m.filterValue);
                    }
                  }
                : undefined
            }
            aria-disabled={isLocked || undefined}
            sx={
              isLocked
                ? { opacity: 0.55, cursor: 'not-allowed', borderRadius: 1 }
                : cardClickable
                  ? {
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease',
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          theme.palette.mode === 'dark' ? 0.12 : 0.04,
                        ),
                      },
                    }
                  : undefined
            }
          >
            <IconContainer sx={iconSxMap[m.iconVariant]}>{m.icon}</IconContainer>
            <StatInfo>
              <StatLabel>
                {isLocked ? (
                  <Box
                    component="span"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <LockIcon sx={{ fontSize: 12 }} />
                    {m.label}
                  </Box>
                ) : (
                  m.label
                )}
              </StatLabel>
              <StatValue>{Number(counts[m.key] ?? 0)}</StatValue>
            </StatInfo>
          </StatItem>
        );

        return isLocked ? (
          <Tooltip key={m.key} title={m.disabledReason} arrow enterDelay={0} placement="top">
            {card}
          </Tooltip>
        ) : (
          card
        );
      })}
    </StatsContainer>
  );
};
